import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Grid,
  Icon,
  Input,
  InputContainer,
  Title,
  UploadPicture,
  RSelect,
  InputError,
  CheckboxContainer,
  CHECKBOX_STATES,
} from '@common/components';
import {
  useUpdateMutation,
  useGetUserMutation,
  useCreateMutation,
} from '@dashboard/api/users';
import { useGetAllProjectsQuery } from '@dashboard/api/projects';
import { getBaseUrlRefresh, getApiUrl } from '@app/api';
import { useTogglePasswordVisibility } from '@auth/hooks/useTooglePasswordVisibility';
import { useInputErrors } from '@app/hooks/useInputErrors';
import {
  isEmail,
  isEmpty,
  passwordsAreEquals,
  isPasswordValid,
} from '@app/util/validations';
import { AlertModal } from '@app/components';
import { useParams, useNavigate } from 'react-router-dom';
import { CentersModal } from '@dashboard/components/CentersModal';
import {
  CREATE_PARAM,
  DEFAULT_PICTURE,
  USER_GLOBAL_ROLES,
  USER_ROLE_CENTER,
  USER_ROLE_PROJECT,
} from '@dashboard/constants';

import profileClasses from './UserEditView.module.scss';

export default function UserEditView() {
  const { t, i18n } = useTranslation('dashboard\\UserProfileView');
  const navigate = useNavigate();

  const { errors, resetErrors, setErrors, updateErrors } = useInputErrors();
  const { uuid } = useParams();
  const [modeCreate, setModeCreate] = useState(true);
  const [user, setUser] = useState({});
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState();
  const [totalCenters, setTotalCenters] = useState(0);
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [touched, setTouched] = useState(false);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [
    accessToNeurodevelopmentIdentification,
    setAccessToNeurodevelopmentIdentification,
  ] = useState(false);

  const [role, setRole] = useState({ value: USER_ROLE_PROJECT });

  const [inputFilePicture, setInputFilePicture] = useState(DEFAULT_PICTURE);

  const [updateUser, { isLoading: isLoadingUpdate }] = useUpdateMutation();
  const [createUser, { isLoading: isLoadingCreate }] = useCreateMutation();
  const [getUser, { isLoading: isLoadingGet }] = useGetUserMutation();
  const { data: dataProjects } = useGetAllProjectsQuery({
    refetchOnFocus: true,
  });

  const updateRolesLocale = (roles) =>
    roles.map((r) => ({ label: t(r.label), value: r.value }));

  const [roles, setRoles] = useState(updateRolesLocale(USER_GLOBAL_ROLES));

  const { passwordVisibility, passwordIcon, handlePasswordVisibility } =
    useTogglePasswordVisibility();

  const {
    passwordVisibility: passwordVisibilityConfirm,
    passwordIcon: passwordIconConfirm,
    handlePasswordVisibility: handlePasswordVisibilityConfirm,
  } = useTogglePasswordVisibility();

  const loadUser = async (id) => {
    const { data } = await getUser(id);
    setUser(data && data.data ? data.data : {});
  };

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setConfirmPassword('');
    setRole(
      roles && roles.length > 0
        ? roles.find((r) => r.value === USER_ROLE_PROJECT)
        : undefined,
    );
    setAccessToNeurodevelopmentIdentification(false);
    resetErrors();
    setTouched(false);
  };

  useEffect(() => {
    resetForm();
    if (uuid !== CREATE_PARAM) {
      setModeCreate(false);
      loadUser(uuid);
    } else {
      setModeCreate(true);
    }
  }, [uuid]);

  useEffect(() => {
    if (dataProjects && dataProjects.data) {
      setProjects(
        dataProjects.data
          ? dataProjects.data.map((p) => ({ label: p.name, value: p.uuid }))
          : [],
      );
      if (selectedProject === undefined) {
        setSelectedProject(
          dataProjects.data && dataProjects.data.length > 0
            ? {
                label: dataProjects.data[0].name,
                value: dataProjects.data[0].uuid,
              }
            : undefined,
        );
      }
    }
  }, [dataProjects]);

  useEffect(() => {
    setEmail(user.email);
    setFirstName(user.first_name);
    setLastName(user.last_name);
    setRole(
      user.is_center_administrator
        ? roles.find((r) => r.value === USER_ROLE_CENTER)
        : roles.find((r) => r.value === USER_ROLE_PROJECT),
    );
    setSelectedCenters(
      user.centers && user.centers.length
        ? user.centers.map((c) => (c.uuid ? c.uuid : c))
        : [],
    );
    setAccessToNeurodevelopmentIdentification(
      user.access_to_neurodevelopment_identification,
    );
    setPassword('');
    setConfirmPassword('');
    setInputFilePicture(
      user?.settings?.avatar
        ? getBaseUrlRefresh(user?.settings?.avatar)
        : DEFAULT_PICTURE,
    );

    if (user.projects && user.projects.length > 0) {
      const project = user.projects[0];
      setSelectedProject({
        label: t(project.name),
        value: project.uuid,
      });
    }

    resetErrors();
  }, [user]);

  const handleChangeProject = (p) => {
    setSelectedProject(p);
    setSelectedCenters([]);
  };

  const handleChangeRole = (p) => {
    setRole(p);
  };

  const onReset = () => {
    resetForm();
    navigate(-1);
  };

  const onValidate = () => {
    let isValid = resetErrors();
    setErrors({});

    if (isEmpty(firstName)) {
      isValid = updateErrors({
        firstName: t('nameValidationRequired'),
      });
    }
    if (isEmpty(lastName)) {
      isValid = updateErrors({
        lastName: t('surnameValidationRequired'),
      });
    }
    if (isEmpty(email)) {
      isValid = updateErrors({
        email: t('emailValidationRequired'),
      });
    } else if (!isEmail(email)) {
      isValid = updateErrors({
        email: t('emailValidationFormat'),
      });
    } else if (
      role &&
      role.value === USER_ROLE_CENTER &&
      selectedCenters.length <= 0
    ) {
      isValid = updateErrors({
        centers: t('centersValidationLength'),
      });
    }

    if (!isEmpty(password) || !isEmpty(confirmPassword)) {
      if (!isPasswordValid(password)) {
        isValid = updateErrors({
          password: t('passwordValidationFormat'),
        });
      }
      if (isEmpty(confirmPassword)) {
        isValid = updateErrors({
          confirmPassword: t('passwordValidationRequired'),
        });
      }
    } else if (isEmpty(password) && modeCreate) {
      isValid = updateErrors({
        password: t('passwordValidationRequired'),
      });
    }

    if (
      confirmPassword &&
      password &&
      !passwordsAreEquals(password, confirmPassword)
    ) {
      isValid = updateErrors({
        confirmPassword: t('passwordValidationEqual'),
      });
    }

    return isValid;
  };
  const [showModal, setShowModal] = useState(false);
  const [showCenters, setShowCenters] = useState(false);

  const onSubmit = async (event) => {
    setTouched(true);
    event.preventDefault();
    if (onValidate()) {
      let profile = {
        project_id: selectedProject.value,
        id: user.uuid,
        email,
        first_name: firstName,
        last_name: lastName,
        access_to_neurodevelopment_identification:
          accessToNeurodevelopmentIdentification,
      };
      if (password) {
        if (modeCreate) {
          profile.password = password;
          profile.password_confirmation = confirmPassword;
        } else {
          const profilePassword = {
            id: user.uuid,
            password,
            password_confirmation: confirmPassword,
          };

          await updateUser(profilePassword);
        }
      }

      if (!inputFilePicture.startsWith(getApiUrl('/'))) {
        profile = {
          ...profile,
          settings: { avatar: inputFilePicture },
        };
      }

      if (
        role &&
        role.value === USER_ROLE_CENTER &&
        selectedCenters.length > 0
      ) {
        profile = {
          ...profile,
          is_center_administrator: true,
          centers: selectedCenters,
        };
      } else {
        profile = {
          ...profile,
          is_center_administrator: false,
          centers: [],
        };
      }

      try {
        const { data } = modeCreate
          ? await createUser(profile)
          : await updateUser(profile);
        if (data.success) {
          setShowModal(true);
          setTouched(false);
        }
      } catch (error) {
        setShowModal(false);
      }
    }
  };

  const fnUpdateRoles = () => {
    setRoles(updateRolesLocale(USER_GLOBAL_ROLES));
  };

  const handleSelectCenters = (newSelectedCenters) => {
    setSelectedCenters(newSelectedCenters);
  };

  useEffect(() => {
    if (touched) {
      onValidate();
    }
  }, [email, password, confirmPassword, firstName, lastName]);

  useEffect(() => {
    i18n.on('languageChanged', fnUpdateRoles);
    i18n.on('loaded', fnUpdateRoles);

    return () => {
      i18n.off('languageChanged', fnUpdateRoles);
      i18n.off('loaded', fnUpdateRoles);
    };
  }, []);

  return (
    <div className={profileClasses.userProfile}>
      <Title color="primary" className={profileClasses.title}>
        {modeCreate ? t('createLabel') : t('editLabel')}
      </Title>
      {user && (
        <form>
          <div className={profileClasses.avatarContainer}>
            <UploadPicture
              className={profileClasses.avatar}
              inputFilePicture={inputFilePicture}
              setInputFilePicture={setInputFilePicture}
            />
          </div>
          <Grid align="top" justify="center">
            <Col xss={12} sm={6}>
              <InputContainer
                className={profileClasses.input}
                classNameContainer={profileClasses.inputContainer}
                placeholder={t('inputNameLabel')}
                name="firstName"
                error={errors.firstName}
              >
                <Input
                  name="firstName"
                  fullWidth
                  type="text"
                  placeholder={t('inputNameLabel')}
                  onChange={setFirstName}
                  value={firstName}
                />
              </InputContainer>
            </Col>
            <Col xss={12} sm={6}>
              <InputContainer
                className={profileClasses.input}
                classNameContainer={profileClasses.inputContainer}
                placeholder={t('inputSurnameLabel')}
                name="lastName"
                error={errors.lastName}
              >
                <Input
                  name="lastName"
                  fullWidth
                  type="text"
                  placeholder={t('inputSurnameLabel')}
                  onChange={setLastName}
                  value={lastName}
                />
              </InputContainer>
            </Col>
            <Col xss={12} sm={6}>
              <InputContainer
                className={profileClasses.input}
                classNameContainer={profileClasses.inputContainer}
                placeholder={t('inputEmailLabel')}
                name="email"
                error={errors.email}
              >
                <Input
                  name="email"
                  fullWidth
                  type="email"
                  placeholder={t('inputEmailLabel')}
                  onChange={setEmail}
                  value={email}
                />
              </InputContainer>
            </Col>
            <Col xss={12} sm={6}>
              <RSelect
                data={projects}
                val={selectedProject}
                className={profileClasses.inputContainer}
                onChange={(e) => handleChangeProject(e)}
                placeholder={t('labelProject')}
              />
            </Col>
            <Col xss={12} sm={6}>
              <RSelect
                data={roles}
                val={role}
                className={profileClasses.inputContainer}
                onChange={(e) => handleChangeRole(e)}
                placeholder={t('labelRole')}
              />
            </Col>
            {role && role.value === USER_ROLE_CENTER && selectedProject && (
              <Col xss={12} sm={6} align="bottom">
                <div className={profileClasses.invisibleLabel} />
                <Button
                  onClick={() => setShowCenters(true)}
                  fullWidth
                  variant="outline"
                  color="accent"
                  className={profileClasses.inputButton}
                  placeholder={t('inputEmailLabel')}
                  name="email"
                  error={errors.email}
                >
                  {t('lblCentersRemaining', {
                    selected: selectedCenters.length,
                    total: totalCenters,
                  })}
                </Button>
                {errors.centers && (
                  <InputError>
                    <div className={profileClasses.invisibleContainer}>
                      {errors.centers}
                    </div>
                  </InputError>
                )}
              </Col>
            )}
            <Col xss={12} sm={6} align="bottom">
              <div className={profileClasses.invisibleLabel} />
              <CheckboxContainer
                id="accessToNeurodevelopmentIdentification"
                label={t('inputAccessToNeurodevelopmentIdentificationLabel')}
                onChange={(e) =>
                  setAccessToNeurodevelopmentIdentification(e.target.checked)
                }
                value="accessToNeurodevelopmentIdentification"
                checked={
                  accessToNeurodevelopmentIdentification
                    ? CHECKBOX_STATES.Checked
                    : CHECKBOX_STATES.Empty
                }
                className={profileClasses.checkboxContainer}
              />
            </Col>
          </Grid>

          <Grid align="top" mode="column">
            <Col xss={12} sm={12}>
              <Title
                type="h2"
                color="primary"
                className={profileClasses.titleMin}
              >
                {t('titleChangePassword')}
              </Title>
              <p className={profileClasses.subtitle}>
                {t('hintChangePassword')}
              </p>
            </Col>
            <Grid align="top" justify="center">
              <Col xss={12} sm={6}>
                <InputContainer
                  className={profileClasses.input}
                  classNameContainer={profileClasses.inputContainer}
                  suffix={
                    <Button minimal onClick={handlePasswordVisibility}>
                      <Icon icon={passwordIcon} />
                    </Button>
                  }
                  placeholder={t('inputPasswordLabel')}
                  name="password"
                  error={errors.password}
                >
                  <Input
                    name="password"
                    fullWidth
                    type={passwordVisibility ? 'text' : 'password'}
                    placeholder={t('inputPasswordLabel')}
                    onChange={setPassword}
                    value={password}
                    autoComplete="new-password"
                  />
                </InputContainer>
              </Col>
              <Col xss={12} sm={6}>
                <InputContainer
                  className={profileClasses.input}
                  classNameContainer={profileClasses.inputContainer}
                  suffix={
                    <Button minimal onClick={handlePasswordVisibilityConfirm}>
                      <Icon icon={passwordIconConfirm} />
                    </Button>
                  }
                  placeholder={t('inputPasswordRepeatLabel')}
                  name="confirmPassword"
                  error={errors.confirmPassword}
                >
                  <Input
                    name="confirmPassword"
                    fullWidth
                    type={passwordVisibilityConfirm ? 'text' : 'password'}
                    placeholder={t('inputPasswordRepeatLabel')}
                    onChange={setConfirmPassword}
                    value={confirmPassword}
                    autoComplete="new-password"
                  />
                </InputContainer>
              </Col>
            </Grid>
          </Grid>
          <Grid
            className={profileClasses.buttonContainer}
            align="middle"
            justify="center"
          >
            <Col xss={12} sm={6}>
              <Button
                className={profileClasses.button}
                variant="flat"
                color="error"
                onClick={onReset}
              >
                {t('btnCancel')}
              </Button>
            </Col>
            <Col xss={12} sm={6}>
              <Button
                className={profileClasses.button}
                variant="flat"
                color="accent"
                onClick={onSubmit}
                disabled={isLoadingUpdate || isLoadingCreate || isLoadingGet}
              >
                {t('btnSave')}
              </Button>
            </Col>
          </Grid>
          {selectedProject && (
            <CentersModal
              project={
                selectedProject
                  ? { name: selectedProject.label, uuid: selectedProject.value }
                  : undefined
              }
              centers={selectedCenters}
              onLoaded={(centers) => setTotalCenters(centers.length)}
              onClose={() => {
                setShowCenters(false);
                if (touched) {
                  onValidate();
                }
              }}
              onConfirm={(centers) => handleSelectCenters(centers)}
              show={showCenters}
            />
          )}
          <AlertModal
            show={showModal}
            type="done"
            title={t(modeCreate ? 'modalTitleNew' : 'modalTitle')}
            text={t(modeCreate ? 'modalTextNew' : 'modalText')}
            onAccept={() => navigate(-1)}
            onClose={() => setShowModal(false)}
          />
        </form>
      )}
    </div>
  );
}
